<script setup>
import config from "../config";
import AccountDashboard from './account_dashboard.vue'
import AccountRewards from './account_rewards.vue'
import AccountPayments from './account_payments.vue'
import { ref, computed, watch, onUnmounted } from "vue";
import PublicActivity from '../components/PublicActivity.vue';
import { financialAccess } from '../services/privacy';
import { useStore } from 'vuex';
import { addressExplorerLink, supportsAuxAccount } from '../services/identity';
import { useRoute } from 'vue-router'
import { mdiOpenInNew, mdiViewDashboard, mdiCashPlus, mdiSendVariantOutline } from '@mdi/js'

const route = useRoute()
const store = useStore();
const identity = computed(() => store.state.account?.miner === route.params.login ? store.state.account : null);
const explorerLink = computed(() => addressExplorerLink(config.explorer.address, identity.value));
const showFinances = computed(() => financialAccess(identity.value, route.params.login));
const showAux = computed(() => config.auxTabsEnabled && showFinances.value && supportsAuxAccount(identity.value, route.params.login));
const publicPage = ref(1);
function refresh() { store.dispatch('fetchAccount', { login: route.params.login, page: publicPage.value, size: 15 }); }

// Rewards and payouts can be opened directly, before the dashboard loads metadata.
watch(() => route.params.login, login => {
    store.commit('BEGIN_ACCOUNT', login);
    publicPage.value = 1;
    refresh();
}, { immediate: true });
const poll = setInterval(() => { if (route.params.page || !showFinances.value) refresh(); }, 10000);
onUnmounted(() => { clearInterval(poll); store.commit('BEGIN_ACCOUNT', ''); });

let tab = ref('dashboard')
const params = computed(() => route.params);

let tabName = ref({
    dashboard: mdiViewDashboard,
    rewards: mdiCashPlus,
    payouts: mdiSendVariantOutline,
});

</script>
<template>
    <div class="text-center">
        <v-btn v-if="explorerLink" class="text-monospace mb-4 none-transform" variant="text"
            :href="explorerLink" target="_blank" rel="noopener noreferrer">
            {{ $filters.tx(route.params.login, 12, route.params.login.length - 12) }}
            <template v-slot:append>
                <v-icon color="warning" :icon="mdiOpenInNew"></v-icon>
            </template>
        </v-btn>
        <div v-else class="text-monospace mb-4 pt-2" style="overflow-wrap: anywhere">{{ route.params.login }}</div>
    </div>
    <v-alert v-if="store.state.accountViewError === 'profile_not_found'" type="warning" variant="tonal" class="mb-4">{{ $t('mp_error_profile_not_found') }}</v-alert>
    <v-alert v-else-if="!identity && store.state.accountViewError" type="warning" variant="tonal" class="mb-4">{{ $t(`mp_error_${store.state.accountViewError}`) }}</v-alert>
    <PublicActivity v-if="identity && !showFinances && store.state.accountViewError !== 'profile_not_found'" :account="identity" @page="publicPage = $event; refresh()" />
    <v-menu v-if="showFinances" class="rounded">
        <template v-slot:activator="{ props }">
            <v-btn block flat variant="tonal" size="large" :prepend-icon="tabName[tab]" v-bind="props"
                class="hidden-md-and-up mb-2 new">
                {{ $t(tab) }}
            </v-btn>
        </template>
        <v-list v-model="tab">
            <v-list-item href="#dashboard" :to="`/account/${params.login}`">
                <v-list-item-title class="text-capitalize">
                    <v-icon size="20" :icon="mdiViewDashboard"></v-icon>
                    {{ $t('dashboard') }}
                </v-list-item-title>
            </v-list-item>
            <v-list-item href="#rewards" :to="`/account/${params.login}/rewards`">
                <v-list-item-title class="text-capitalize">
                    <v-icon size="20" :icon="mdiCashPlus"></v-icon>
                    {{ $t('rewards') }}
                </v-list-item-title>
            </v-list-item>
            <v-list-item href="#payouts" :to="`/account/${params.login}/payouts`">
                <v-list-item-title class="text-capitalize">
                    <v-icon size="20" :icon="mdiSendVariantOutline"></v-icon>
                    {{ $t('payouts') }}
                </v-list-item-title>
            </v-list-item>
        </v-list>
    </v-menu>

    <v-tabs v-if="showFinances" v-model="tab" show-arrows="" :mobile-breakpoint="0" grow class="hidden-sm-and-down mb-3 border rounded">
        <v-tab :prepend-icon="mdiViewDashboard" value="dashboard" :to="`/account/${params.login}`">
            {{ $t('dashboard') }}
        </v-tab>
        <v-tab :prepend-icon="mdiCashPlus" value="rewards" :to="`/account/${params.login}/rewards`">
            {{ $t('rewards') }}
        </v-tab>
        <v-tab :prepend-icon="mdiSendVariantOutline" value="payouts" :to="`/account/${params.login}/payouts`">
            {{ $t('payouts') }}
        </v-tab>
    </v-tabs>

    <div v-if="showFinances && route.params.page == null" class="pt-1">
        <AccountDashboard :key="route.params.login" />
    </div>
    <div v-if="showFinances && route.params.page === 'rewards'" class="pt-1">
        <AccountRewards :key="route.params.login" :show-aux="showAux" />
    </div>
    <div v-if="showFinances && route.params.page === 'payouts'" class="pt-1">
        <AccountPayments :key="route.params.login" :show-aux="showAux" />
    </div>
</template>
